import { useContext, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import store from 'client/store/store';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    setSelected,
    addMapMode,
    removeMapMode,
    setAOIError,
    setGotGeoLocationResult,
    setMenuOpen,
    setTrackLocation
} from 'client/store/mapSlice/mapSlice';
import { aggregateAssets, clearAssets } from 'client/store/assetsSlice/assetsSlice';
import {
    clearSupplementaryData,
    setFeatureIdList
} from 'client/store/SupplimentaryDataSlice/supplementaryDataSlice';
import { clearAssetOwners, setAssetOwners, setAssetOwnersError } from 'client/store/assetOwnersSlice/assetOwnersSlice';
import { selectMessagesByLocation } from 'client/store/systemMessageSlice/systemMessageSlice';
import { useActionData, useLoaderData, useSubmit } from 'react-router';
import { Helmet } from 'react-helmet-async';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { bbox as turfBbox } from '@turf/turf';
import { buffer as bufferExtent } from 'ol/extent';
import PhaseBanner from 'client/components/suc/PhaseBanner/PhaseBanner';
import MapToolBar from './components/MapToolBar/MapToolBar';
import MapMenu from './components/MapMenu/MapMenu';
import MapLoading from './components/MapLoading/MapLoading';
import SnackBars from 'client/components/ui/SnackBars/SnackBars';
import SnackBar from 'client/components/ui/Snackbar/SnackBar';
import MapInteractions from './components/MapInteractions/MapInteractions';
import MapInfo from './components/MapInfo/MapInfo';
import OSBranding from './components/OSBranding/OSBranding';
import NIBranding from './components/NIBranding/NIBranding';
import AOIErrorSnackBar from './components/SnackBars/AOIErrorSnackBar/AOIErrorSnackBar';
import BookmarkSnackBar from './components/SnackBars/BookmarkSnackBar/BookmarkSnackBar';
import EnhancedMeasuresSnackBar from './components/SnackBars/EnhancedMeasuresSnackBar/EnhancedMeasuresSnackBar';
import { TimedAlertSnackBar } from './components/SnackBars/TimedAlertSnackBar/TimedAlertSnackBar';
import AssetsHiddenSnackBar from './components/SnackBars/AssetsHiddenSnackBar/AssetsHiddenSnackBar';
import AOIMarkUpSnackBar from './components/SnackBars/MobileSnackBar/AOIMarkUpSnackBar';
import CreatingObservationSnackBar from './components/SnackBars/CreatingObservationSnackBar/CreatingObservationSnackBar';
import IsolatedViewsSnackBar from './components/SnackBars/IsolatedViewsSnackBar/IsolatedViewsSnackBar';
import SupplementaryDataErrorSnackBar from './components/SnackBars/SupplementaryDataErrorSnackBar/SupplementaryDataErrorSnackBar';
import { AssetOwnerService, FeaturesService } from 'client/services';
import { useLineViewDimensions } from 'client/utils/auth/userPermissions';
import { mapModes } from 'client/utils/constants/appStates';
import { SAVE_OBSERVATION_PAGE } from 'client/utils/routes/routeConstants';
import {
    Attribution,
    Controls,
    Map,
    MapContext,
    ScaleLine,
    View,
    Zoom
} from 'os-elements';
import { MyLocation } from './components/MyLocation/MyLocation';
import { DESKTOP } from 'client/utils/constants/GDS/js/devices';
import useDeviceType from 'client/utils/helpers/DeviceType/useDeviceType';
import CreateObservationButton from './components/CreateObservationButton/CreateObservationButton';
import { getGeoJsonGeometry } from 'client/utils/map/geometryUtils';
import { setAlertProps, setTimedContent } from 'client/store/alertSlice/alertSlice';
import styles from './MapPage.module.scss';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';
import { bufferedPoint } from 'client/utils/helpers/Aoi';
import CustomMappingSnackBar from 'client/pages/MapPage/components/SnackBars/CustomMappingOrAnnotationsSnackBarIntl/CustomMappingSnackBar';
import { useGeolocation } from './components/MyLocation/useGeolocation';
import { MobileDrawModal } from 'client/pages/MapPage/components/MapToolBar/DrawButton/MobileDrawModal/MobileDrawModal.js';
import useAriaZoomControls from 'client/utils/map/applyAriaZoomControls';
import DynamicLegendButton from 'client/pages/MapPage/components/DynamicLegend/DynamicLegendButton/DynamicLegendButton';
import DynamicLegend from 'client/pages/MapPage/components/DynamicLegend/DynamicLegend';
import useLocationInUrl from 'client/pages/MapPage/hooks/useLocationInUrl';
import HighContrastButton from 'client/pages/MapPage/components/HighContrastButton/HighContrastButton';
import { useDarkMode } from 'client/utils/helpers/DarkMode/useDarkMode';
import { EnhancedMeasuresModal } from 'client/pages/MapPage/components/EnhancedMeasuresModal/EnhancedMeasuresModal';
import { selectBufferRadiusMetres, setBufferRadiusMetres, setDrawType } from 'client/store/drawSlice/drawSlice';
import { LINE } from 'client/utils/constants/symbolName';
import { LINE_STRING, MULTI_LINE_STRING, POINT } from 'client/utils/constants/geometryType';
import FloatingDrawPanel from './components/FloatingDrawPanel/FloatingDrawPanel';
import PdfsDisabledSnackBar from './components/SnackBars/PdfsDisabledSnackBar/PdfsDisabledSnackBar';
import MapLayers from './components/MapLayers/MapLayers';
import { useScreenBoundingBox } from './hooks/useScreenBoundingBox';
import { observeSystemMessageHeight } from 'client/utils/helpers/SystemMessageHeight/SystemMessageHeight';

const { DRAWING, AOI, LOADING, ADDING_OBSERVATION } = mapModes;
const drawSource = new VectorSource({ wrapX: false });

const MapPage = () => {
    const dispatch = useDispatch();
    const mapMode = useSelector(state => state.map?.mode);
    const bufferRadiusMetres = useSelector(selectBufferRadiusMetres);
    const typeNames = useSelector(state => state.layers?.result?.typeNames || null);
    const alert = useSelector(state => state.alert);
    const aoiSource = useSelector(state => state.aoi?.source);
    const mapView = useSelector(state => state.mapView?.result?.mapView);
    const trackLocation = useSelector(state => state.map?.trackLocation);
    const mapMessages = useSelector(selectMessagesByLocation('map'), shallowEqual);
    const hasMessages = mapMessages.length > 0;
    const lineViewDimensions = useLineViewDimensions();
    const { map } = useContext(MapContext);
    const { epsgCode, bookmark, enquiry, reloadAOI } = useLoaderData() || {};
    const actionData = useActionData() || {};
    const intl = useIntl();
    const deviceType = useDeviceType();
    const submit = useSubmit();

    const [highContrast, setDarkMode] = useDarkMode();
    // Store the draft observation in state so that we can reset it when exiting observation mode.
    const [draftObservation, setDraftObservation] = useState(actionData.draftObservation || {});
    const [hasPannedToUserLocation, setHasPannedToUserLocation] = useState(false);
    const [positionFeature, setPositionFeature] = useState(new Feature());
    const [showCreateMobileAOIDialog, setCreateMobileAOIDialog] = useState(false);
    const [drawMobileArea, setDrawMobileArea] = useState(false);
    const [hasClearedAOI, setHasClearedAOI] = useState(false);
    const [mapSystemMessageHeight, setMapSystemMessageHeight] = useState(0);
    const mapContainerRef = useRef(null);

    useEffect(() => {
        if (!hasMessages) {
            setMapSystemMessageHeight(0);
            return undefined;
        }

        return observeSystemMessageHeight({
            location: 'map',
            onUpdate: setMapSystemMessageHeight
        });
    }, [hasMessages, mapMessages]);

    useScreenBoundingBox();
    // os-elements VectorLayer uses function.toString() to detect prop changes
    // Set up URL to update with the current location, if the user is not coming from a bookmark or enquiry
    const hasAOIFromBookmark = bookmark?.geometry?.coordinates.length > 0;
    const hasAOIFromEnquiry = enquiry?.geometry?.coordinates.length > 0;
    const hasBeenGivenAnAOI = hasAOIFromBookmark || hasAOIFromEnquiry;
    const shouldReplaceUrlWithLocationHash = !(mapMode.includes(LOADING) || (hasBeenGivenAnAOI && !hasClearedAOI));
    const isEditingObservation = Boolean(draftObservation.systemId) || Boolean(draftObservation.geometry?.coordinates);
    useLocationInUrl(shouldReplaceUrlWithLocationHash);

    const openMenuIfOnDesktop = () => {
        dispatch(setMenuOpen(deviceType === DESKTOP));
    };

    // Close the menu when the map page loads (moved from loader to avoid render-time dispatch)
    useEffect(() => {
        dispatch(setMenuOpen(false));
    }, [dispatch]);

    // Set the map tab index to zero for accessible map controls
    useEffect(() => { if (map) { map.getViewport().setAttribute('tabindex', 0); } }, [map]);
    // Apply Aria labels to Zoom Controls for screen-reader accessibility
    useAriaZoomControls();

    // Set up geolocation handlers
    useGeolocation({
        projection: `EPSG:${epsgCode}`,
        trackLocation,
        options: {
            enableHighAccuracy: deviceType !== DESKTOP,
            timeout: 10000,
            maximumAge: 0
        },
        onPosition: (result) => {
            // Set this so that we do not override the 'pan to location' with a 'pan to AOI' later
            setHasPannedToUserLocation(true);
            // Pan and zoom to the location
            const view = map.getView();
            view.setCenter([result.longitude, result.latitude]);
            view.setZoom(20);
            // Set the position feature
            const locationMarker = new Feature();
            locationMarker.setGeometry(new Point([result.longitude, result.latitude]));
            setPositionFeature(locationMarker);
            // Set state fields so we can apply loading state to button in between pressing and getting a location
            dispatch(setGotGeoLocationResult(true));
            // and switch tracking off to allow us to re-press button
            dispatch(setTrackLocation(false));
        },
        onError: (result) => {
            dispatch(setTrackLocation(false));
            dispatch(setGotGeoLocationResult(true));
            if (result.code === 1) {
                dispatch(setTimedContent(<FormattedMessage id='mapPage.location.error' />));
            } else if (result.code === 3) {
                dispatch(setTimedContent(<FormattedMessage id='mapPage.location.errorTimeOut' />));
            }
        }
    });

    // Load the AOI from the bookmark, enquiry, or observation draft:
    const handleClearAoi = () => {
        setHasClearedAOI(true);
    };
    useEffect(() => {
        if (!aoiSource || !typeNames?.length) {
            return;
        }

        if (!actionData.draftObservation) {
            dispatch(removeMapMode(mapModes.ADDING_OBSERVATION));
        }

        const aoiExtent = aoiSource.getExtent();
        if (aoiExtent && aoiExtent.every(isFinite)) {
            if (!hasPannedToUserLocation) {
                // re centre on AOI if present (e.g. created bookmark, going 'back' from i2d or create-observation)
                map.getView().fit(aoiExtent, { padding: [50, 50, 50, 50] });
            }
        }
        let { geometry, mapExtent } = bookmark || enquiry || { geometry: null, mapExtent: null };

        if (actionData.draftObservation?.systemId) {
            const aoiAroundObservation = bufferedPoint(actionData.draftObservation.geometry.coordinates);
            const geojson = JSON.parse(new GeoJSON().writeFeatures([aoiAroundObservation]));
            geometry = geojson.features[0].geometry;
            mapExtent = geometry.coordinates;
        }

        if (!geometry && reloadAOI) {
            // read aoi geometry
            const aoiGeometry = getGeoJsonGeometry(map, 'aoi');
            if (aoiGeometry) {
                geometry = JSON.parse(aoiGeometry);
            }
        }

        if (reloadAOI) {
            FeaturesService.clearFeatureCache();
        }
        // Important to attach these listeners _before_ setAOIGeometry is called
        aoiSource.on('change', handleChangeAOI);
        aoiSource.on('clear', handleClearAoi);

        // Restore draw type and buffer for LineString bookmarks so that
        // handleChangeAOI picks up the correct buffer when revealing assets.
        if (bookmark && geometry) {
            const geomType = geometry.type;
            if (geomType === LINE_STRING || geomType === MULTI_LINE_STRING) {
                dispatch(setDrawType(LINE));
                if (Number.isFinite(bookmark.buffer)) {
                    dispatch(setBufferRadiusMetres(bookmark.buffer));
                }
            }
        }

        if (geometry?.coordinates?.length || mapExtent) {
            setAOIGeometry({ geometry, mapExtent });
        }

        return () => {
            if (aoiSource) {
                aoiSource.un('change', handleChangeAOI);
                aoiSource.un('clear', handleClearAoi);
            }
        };
    }, [bookmark, enquiry, reloadAOI, typeNames, aoiSource]);

    // Handlers for adding an observation
    useEffect(() => {
        if (!map || !mapMode.includes(mapModes.ADDING_OBSERVATION)) {
            // if not editing an existing observation (from an edit action) then can drop out
            if (!actionData.draftObservation?.systemId) {
                setDraftObservation({});
                return;
            }
        }
        map.on('click', handleAddObservation);
        return () => {
            map.un('click', handleAddObservation);
        };
    }, [map, mapMode]);

    /**
     * previously called loadAOI, but this doesn't load any features it just sets the geometry for the AOI layer
     * The feature-loading is done in getFeatures, which is triggered by the aoiSource change event
     */
    const setAOIGeometry = ({ geometry, mapExtent }) => {
        aoiSource?.clear();
        dispatch(setSelected([]));
        if (geometry) {
            mapExtent = turfBbox(geometry);
            const geomType = geometry.type;
            if (geomType === LINE_STRING || geomType === MULTI_LINE_STRING || geomType === POINT) {
                const buffer = bookmark?.buffer;
                if (Number.isFinite(buffer) && buffer > 0) {
                    mapExtent = bufferExtent(mapExtent, buffer);
                }
            }
            const feature = new GeoJSON().readFeature({ geometry: { ...geometry }, type: 'Feature' });
            aoiSource?.addFeature(feature);
            setHasClearedAOI(false);
        } else {
            clearCurrentFeatures();
        }
        if (map && mapExtent) {
            map.getView().fit(mapExtent, { padding: [50, 50, 50, 50] });
        }
    };

    /**
     * This is triggered by the aoiSource change event, so that whenever the AOI changes it automatically reads
     * the AOI geometry and fetches the features for the AOI
     * (previously called updateAOI)
     * */
    const handleChangeAOI = async (source) => {
        const features = source?.target?.getFeatures();
        dispatch(clearAssetOwners());
        if (features?.length > 0 && typeNames?.length > 0) {
            const aoi = features[0];
            const geomType = aoi.getGeometry()?.getType();
            const isLine = geomType === 'LineString' || geomType === 'MultiLineString';

            // Read the current buffer directly from the store to avoid stale closures,
            // since this handler is registered as an OL event listener outside the React render cycle.
            const currentBuffer = store.getState()?.draw?.bufferRadiusMetres;
            const effectiveBuffer = Number.isFinite(currentBuffer) ? currentBuffer : bufferRadiusMetres;

            // AOI_VIEW_LINE validation: check line extent and buffer
            // against the user's view permissions before sending the request.
            if (isLine) {
                const extent = aoi.getGeometry().getExtent();
                const extentWidth = extent[2] - extent[0];
                const extentHeight = extent[3] - extent[1];
                const lineLength = Math.max(extentWidth, extentHeight);

                if (lineLength > lineViewDimensions.length ||
                    effectiveBuffer > lineViewDimensions.buffer) {
                    dispatch(setAlertProps({
                        open: true,
                        severity: 'error',
                        content: intl.formatMessage({ id: 'mapPage.error.areaTooLarge' })
                    }));
                    return;
                }
            }

            // Pass the buffer radius to the backend for line geometries so it
            // uses the user's chosen width rather than the hardcoded default.
            const options = isLine ? { buffer: effectiveBuffer } : {};
            await getFeatures(aoi, options);
            openMenuIfOnDesktop();
        } else {
            dispatch(clearAssets());
            clearCurrentFeatures();
        }
    };

    /**
     * Fetches the features for the AOI and sets
     * the results in the redux store, including asset owners and supplementary data
     */
    const getFeatures = async (aoi, options = {}) => {
        handleNewFeatures();
        const requestAoi = aoi.clone();
        const requestGeometry = requestAoi.getGeometry();
        if (requestGeometry.getType() === LINE_STRING && requestGeometry.flatCoordinates.length === 2) {
            requestAoi.setGeometry(new Point(requestGeometry.flatCoordinates));
        }

        // Load the features for the AOI
        return FeaturesService.getFeatures(requestAoi, featureBatch => dispatch(aggregateAssets(featureBatch)), options).then(({ featureIdList }) => {
            // Set the redux state to capture that we have features and are in AOI mode
            dispatch(removeMapMode(LOADING));
            dispatch(addMapMode(AOI));
            dispatch(clearSupplementaryData());
            dispatch(clearAssetOwners());
            // overwrite the featureIdList so we can trigger a retry if it errors
            dispatch(setFeatureIdList(featureIdList));

            // Get the asset owners for the features, and set the results in the redux store
            const dataProviderIds = featureIdList.map(featureId => featureId?.dataProviderId);
            AssetOwnerService.getAssetOwners(requestAoi, dataProviderIds).then(assetOwners => dispatch(setAssetOwners(assetOwners))).catch(e => dispatch(setAssetOwnersError(e)));
        }).catch((err) => {
            dispatch(setAOIError(err));
            dispatch(removeMapMode(LOADING));
            dispatch(removeMapMode(DRAWING));
            dispatch(clearAssetOwners());
            dispatch(clearSupplementaryData());
            dispatch(clearAssets());
            clearCurrentFeatures();
            drawSource.clear();
            aoiSource.clear();
        });
    };

    const clearCurrentFeatures = () => {
        dispatch(removeMapMode(AOI));
    };

    const handleNewFeatures = () => {
        dispatch(addMapMode(LOADING));
    };

    const handleAddObservation = async (evt) => {
        if (!map || !mapMode.includes(ADDING_OBSERVATION)) {
            // if not editing an existing observation (from an edit action) then can drop out
            if (!actionData.draftObservation?.systemId) {
                // (this shouldn't ever happen)
                return;
            }
        }

        // Warn if outside AOI
        const aoiGeometry = map.getLayerById('aoi')?.getSource()?.getFeatures()[0]?.getGeometry();
        const isWithinAOI = aoiGeometry.intersectsCoordinate(evt.coordinate);
        if (!aoiGeometry || !isWithinAOI) {
            dispatch(setTimedContent(intl.formatMessage({ id: 'mapPage.error.addObservationInsideAOI' })));
            return;
        }

        // Close timed alerts
        dispatch(setTimedContent());

        // Create a draft observation at the clicked point
        // either empty or based on the draft in the action data
        // which will be populated if we're editing an observation
        const newDraft = { ...draftObservation };
        // Set the location of the observation
        newDraft.geometry = {
            type: 'Point',
            coordinates: evt.coordinate,
            crs: {
                type: 'name',
                properties: {
                    name: `EPSG:${epsgCode}`
                }
            }
        };

        // Forget the draft now we are submitting
        setDraftObservation({});
        // Submit draft to create page, along with the AOI
        const formData = new FormData();
        // Preserve the AOI geometry so we can draw the AOI on the minimap and request assets to display again
        const aoiGeoJson = getGeoJsonGeometry(map, 'aoi');
        formData.append('geometry', aoiGeoJson);
        formData.append('draftObservation', JSON.stringify(newDraft));
        submit(formData, {
            action: SAVE_OBSERVATION_PAGE,
            method: 'put'
        });
    };

    return (
        <div
            role='main'
            data-testid="mapPage"
            className={`${styles.page} ${hasMessages ? styles.withMessages : ''}`}
            style={{ '--map-system-message-height': `${mapSystemMessageHeight}px` }}
        >
            <Helmet>
                <meta charSet="utf-8" />
                <title>{intl.formatMessage({ id: 'mapPage.title' })}</title>
            </Helmet>
            <div className={styles.phaseBanner}>
                <PhaseBanner />
            </div>
            <MapToolBar setCreateMobileAOIDialog={setCreateMobileAOIDialog} isEditingObservation={isEditingObservation} />
            <div className={styles.container}>
                {/* zIndex - 1 less than draw mode */}
                <EnhancedMeasuresModal zIndex={49} />
                <MobileDrawModal showCreateMobileAOIDialog={showCreateMobileAOIDialog} setCreateMobileAOIDialog={setCreateMobileAOIDialog} setDrawMobileArea={setDrawMobileArea} />
                <MapMenu />
                <div className={styles.map} ref={mapContainerRef}>
                    <MapLoading />
                    <SnackBars role="alert">
                        <PdfsDisabledSnackBar />
                        {alert?.open && <SnackBar variant={alert.severity}>{alert.content}</SnackBar>}
                        <TimedAlertSnackBar />
                        <AssetsHiddenSnackBar />
                        <AOIMarkUpSnackBar />
                        <CreatingObservationSnackBar />
                        <AOIErrorSnackBar />
                        <BookmarkSnackBar />
                        <EnhancedMeasuresSnackBar />
                        <IsolatedViewsSnackBar />
                        <SupplementaryDataErrorSnackBar />
                        <CustomMappingSnackBar />
                    </SnackBars>
                    <Map>
                        <View {...mapView} showFullExtent={true} enableRotation={false} />
                        <Controls>
                            <Zoom />
                            <ScaleLine />
                            <Attribution />
                            {epsgCode === '27700' && <OSBranding />}
                            {epsgCode === '29903' && <NIBranding />}
                            <MyLocation />
                            <HighContrastButton highContrast={highContrast} setHighContrast={setDarkMode} />
                            <DynamicLegendButton />
                            {!draftObservation.geometry ? <CreateObservationButton /> : null}
                        </Controls>
                        <MapInteractions />
                        <DynamicLegend />
                        <MapLayers handleChangeAOI={handleChangeAOI} positionFeature={positionFeature} drawMobileArea={drawMobileArea} setDrawMobileArea={setDrawMobileArea} />
                    </Map>
                    <FloatingDrawPanel containerRef={mapContainerRef} />
                    <MapInfo />
                </div>
            </div>
        </div>
    );
};

export default MapPage;
